import React from 'react';
import { Badge, Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { PeriodComparisonTableProps, ComparisonData } from './types';
import { TrendIcon, TableText } from './styles';
import { TREND_COLORS, TREND_ICONS, DISPLAY_DATE_RANGE_FORMAT, DISPLAY_DATE_FORMAT } from './constants';
import Table from '../../../commonComponents/Table';
import { useTheme } from '../../../context/ThemeContext';

const PeriodComparisonTable: React.FC<PeriodComparisonTableProps> = ({
  comparisons,
  isLoading,
}) => {
  const { isDarkMode } = useTheme();
  const themeMode: 'light' | 'dark' = isDarkMode ? 'dark' : 'light';

  const getTrendIcon = (trend: string) => {
    const trendKey = (trend || 'neutral') as keyof typeof TREND_ICONS;
    const icon = TREND_ICONS[trendKey];
    const color = TREND_COLORS[trendKey];

    return (
      <TrendIcon color={color}>
        <FontAwesomeIcon icon={icon} />
      </TrendIcon>
    );
  };

  const columns: ColumnsType<ComparisonData> = [
    {
      title: 'Period',
      dataIndex: 'label',
      key: 'label',
      render: (text: string) => (
        <TableText fontWeight={600} color="#1890ff">
          {text}
        </TableText>
      ),
    },
    {
      title: 'Date Range',
      key: 'dateRange',
      render: (_, record: ComparisonData) => (
        <span>
          {moment(record.startDate).format(DISPLAY_DATE_RANGE_FORMAT)} -{' '}
          {moment(record.endDate).format(DISPLAY_DATE_FORMAT)}
        </span>
      ),
    },
    {
      title: 'Times Sold',
      dataIndex: 'totalTimesSold',
      key: 'totalTimesSold',
      render: (value: number) => (
        <TableText fontWeight={600}>{value.toLocaleString()}</TableText>
      ),
    },
    {
      title: 'Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (value: number) => (
        <TableText fontWeight={600} color="#52c41a">
          ${value.toLocaleString()}
        </TableText>
      ),
    },
    {
      title: 'Volume Growth',
      dataIndex: 'volumeGrowth',
      key: 'volumeGrowth',
      render: (value: number, record: ComparisonData) => (
        <TableText
          color={value >= 0 ? TREND_COLORS.up : TREND_COLORS.down}
        >
          {value > 0 ? '+' : ''}
          {value}%
        </TableText>
      ),
    },
    {
      title: 'Revenue Growth',
      dataIndex: 'revenueGrowth',
      key: 'revenueGrowth',
      render: (value: number, record: ComparisonData) => (
        <TableText
          color={value >= 0 ? TREND_COLORS.up : TREND_COLORS.down}
        >
          {value > 0 ? '+' : ''}
          {value}%
        </TableText>
      ),
    },
    {
      title: 'Trend',
      dataIndex: 'trend',
      key: 'trend',
      render: (trend: string) => <Space>{getTrendIcon(trend)}</Space>,
    },
    {
      title: 'Status',
      dataIndex: 'itemFound',
      key: 'itemFound',
      render: (itemFound: boolean) => (
        <Badge
          status={itemFound ? 'success' : 'error'}
          text={itemFound ? 'Found' : 'Not Found'}
        />
      ),
    },
  ];

  return (
    <Table<ComparisonData>
      columns={columns}
      dataSource={comparisons}
      rowKey={(record) => `comparison-${record.label}-${record.startDate}-${record.endDate}`}
      pagination={false}
      loading={isLoading}
      emptyText="No comparisons found"
      emptyDescription="No period comparisons available"
      scroll={{ x: true, y: 600 }}
      headerBackgroundColor={isDarkMode ? '#4A5568' : '#fafafa'}
      removeVerticalBorders={true}
      thPadding="16px 12px"
      tdPadding="16px 12px"
      firstChildBorderRadius="8px"
      border="0"
      borderBottomLeftRight={true}
      theme={themeMode}
    />
  );
};

export default PeriodComparisonTable;
